import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Switch,
  FormControlLabel,
  Tooltip,
  InputAdornment,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import AdminService from '../../services/AdminService';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // User form state
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    isActive: true,
    roles: []
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  
  // Delete confirmation dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Role filter state
  const [roleFilter, setRoleFilter] = useState('');
  
  // Status filter state
  const [statusFilter, setStatusFilter] = useState('');
  
  // Success message state
  const [openSnackbar, setOpenSnackbar] = useState(false);
  
  // Debugging state
  const [debugMode, setDebugMode] = useState(false);
  
  // Effect to run on component mount
  useEffect(() => {
    console.log('Component mounted - fetching initial user data');
    fetchUsers();
  }, []);  // Empty dependency array ensures it only runs once on mount
  
  // Effect to run when filters change
  useEffect(() => {
    console.log('Filters changed - fetching updated user data');
    fetchUsers();
  }, [roleFilter, statusFilter, searchTerm]);  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all users without filters (backend doesn't support filtering yet)
      const filters = {
        page: 1,
        limit: 1000 // Get all users
      };
      
      console.log('Fetching users...');
      
      // Check if API is available
      const isApiConnected = await checkApiConnection();
      console.log('API connection status:', isApiConnected ? 'Connected' : 'Disconnected');
      
      // Call the API to get users
      let usersData = [];
      
      if (isApiConnected) {
        try {
          // First attempt: Standard API call
          console.log('Attempting standard API call');
          const response = await AdminService.getUsers(filters);
          console.log('API Response:', response);
          
          // Process data based on the format returned
          if (Array.isArray(response)) {
            usersData = response;
          } else if (response && response.users) {
            usersData = response.users;
          } else if (response) {
            // Handle case where the response might be an object or other structure
            if (typeof response === 'object' && Object.keys(response).length > 0) {
              // Try to extract user-like objects
              if (response.username) {
                // Single user object
                usersData = [response];
              } else {
                // Try to find users in the object
                const possibleUsers = Object.values(response).filter(
                  item => item && typeof item === 'object' && item.username
                );
                if (possibleUsers.length > 0) {
                  usersData = possibleUsers;
                }
              }
            }
          }
        } catch (apiError) {
          console.error('Standard API call failed:', apiError);
          
          try {
            // Second attempt: Direct API call as fallback
            console.log('Attempting direct API call as fallback');
            usersData = await AdminService.getUsersFromJsonFile();
          } catch (directError) {
            console.error('Direct API call failed:', directError);
            throw new Error('Failed to fetch users data from all available methods');
          }
        }
      } else {
        // API is not connected, use direct method
        console.log('API not connected, using direct method');
        try {
          usersData = await AdminService.getUsersFromJsonFile();
        } catch (directError) {
          console.error('Direct API call failed:', directError);
          throw new Error('Failed to fetch users data - API is unavailable');
        }
      }
      
      console.log('Raw users data:', usersData);
      
      if (usersData && usersData.length > 0) {
        // Transform the users data to match our UI format
        const transformedUsers = usersData.map(user => ({
          id: user.username, // Using username as ID since it's unique
          username: user.username,
          email: user.email || '',
          roles: user.roles || [],
          isActive: user.disabled === undefined ? true : !user.disabled,
          lastLogin: user.last_login
        }));
        
        console.log('Transformed Users:', transformedUsers);
        
        // Apply client-side filtering
        let filteredUsers = transformedUsers;
        
        // Filter by search term
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          filteredUsers = filteredUsers.filter(user => 
            user.username.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower)
          );
        }
        
        // Filter by role
        if (roleFilter) {
          filteredUsers = filteredUsers.filter(user => 
            user.roles.includes(roleFilter)
          );
        }
        
        // Filter by status
        if (statusFilter) {
          const isActive = statusFilter === 'active';
          filteredUsers = filteredUsers.filter(user => 
            user.isActive === isActive
          );
        }
        
        console.log('Filtered Users:', filteredUsers);
        
        setUsers(filteredUsers);
        setTotalUsers(filteredUsers.length);
      } else {
        console.log('No users found or empty response');
        setUsers([]);
        setTotalUsers(0);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      // Extract error message from response if available
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load users';
      setError(`Failed to load users: ${errorMessage}`);
      setLoading(false);
    }
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
    const handleOpenUserDialog = (mode, user = null) => {
    if (mode === 'edit' && user) {
      setSelectedUser(user);
      setUserForm({
        username: user.username || '',
        email: user.email || '',
        password: '',
        confirmPassword: '',
        isActive: user.isActive !== false, // In the API, "disabled" is used so we invert it
        roles: user.roles || []
      });
    } else {
      // Reset form for add mode
      setSelectedUser(null);
      setUserForm({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        isActive: true,
        roles: ['viewer'] // Default role for new users
      });
    }
    
    setDialogMode(mode);
    setFormErrors({});
    setShowPassword(false);
    setOpenUserDialog(true);
  };
  
  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
  };
  
  const handleUserFormChange = (event) => {
    const { name, value, checked } = event.target;
    
    if (name === 'isActive') {
      setUserForm(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'roles') {
      setUserForm(prev => ({ ...prev, [name]: value }));
    } else {
      setUserForm(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear validation error when field is updated
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
    const validateForm = () => {
    const errors = {};
    
    if (!userForm.username.trim()) {
      errors.username = 'Username is required';
    } else if (dialogMode === 'add' && userForm.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    }
    
    if (!userForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(userForm.email)) {
      errors.email = 'Invalid email address';
    }
    
    if (dialogMode === 'add') {
      // For new users, password is required
      if (!userForm.password) {
        errors.password = 'Password is required';
      } else if (userForm.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }
      
      if (userForm.password !== userForm.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    } else if (userForm.password) {
      // In edit mode, password is optional, but if provided it must be valid
      if (userForm.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      }
      
      if (userForm.password !== userForm.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }
    
    if (userForm.roles.length === 0) {
      errors.roles = 'At least one role must be selected';
    }
    
    // Special validation for admin users
    if (selectedUser && 
        selectedUser.username === 'admin' &&
        !userForm.roles.includes('admin')) {
      errors.roles = 'Cannot remove admin role from the default admin user';
    }
    
    return errors;
  };
    const handleSaveUser = async () => {
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    try {
      setLoading(true);
      
      // Prepare the user data in the format expected by the API
      const userData = {
        username: userForm.username,
        email: userForm.email,
        full_name: userForm.username, // Using username as full name if not provided
        roles: userForm.roles,
      };
      
      // Add password only if it's provided (required for new users)
      if (userForm.password) {
        userData.password = userForm.password;
      }
      
      // Add disabled status (opposite of isActive)
      userData.disabled = !userForm.isActive;
      
      let successMessage = '';
      
      if (dialogMode === 'add') {
        // Create new user
        await AdminService.createUser(userData);
        successMessage = `User ${userData.username} created successfully`;
      } else if (selectedUser) {
        // Update existing user
        // For editing an existing user, we need to handle roles and status separately
        
        // Update disabled status if changed
        if (selectedUser.isActive !== userForm.isActive) {
          // The API uses "disabled" which is the opposite of "isActive"
          await AdminService.updateUserStatus(selectedUser.username, !userForm.isActive);
        }
        
        // Update roles if changed
        if (JSON.stringify(selectedUser.roles) !== JSON.stringify(userForm.roles)) {
          await AdminService.updateUserRoles(selectedUser.username, userForm.roles);
        }
        
        // Update email if changed
        if (selectedUser.email !== userForm.email) {
          await AdminService.updateUser(selectedUser.username, {
            email: userForm.email
          });
        }
        
        // Update password if provided (use separate password reset endpoint)
        if (userForm.password) {
          await AdminService.resetUserPassword(selectedUser.username, userForm.password);
        }
        
        successMessage = `User ${selectedUser.username} updated successfully`;
      }      // Close dialog and refresh the user list      handleCloseUserDialog();
      // Force immediate refresh after user creation/update
      console.log('Refreshing after save user operation');
      forceRefresh();
      setError(null);
      setSuccessMessage(successMessage);
      setOpenSnackbar(true);
    } catch (err) {
      console.error('Error saving user:', err);
      // Extract error message from response if available
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
      setError(`Failed to save user: ${errorMessage}`);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setOpenDeleteDialog(true);
  };
  
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setUserToDelete(null);
  };
    const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setLoading(true);
      await AdminService.deleteUser(userToDelete.username);      handleCloseDeleteDialog();
      // Force immediate refresh after user deletion
      console.log('Refreshing after delete user operation');
      forceRefresh();
      setError(null);
      setSuccessMessage(`User ${userToDelete.username} deleted successfully`);
      setOpenSnackbar(true);
    } catch (err) {
      console.error('Error deleting user:', err);
      
      // Extract error message from response if available
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
      
      // Handle special case where deletion is rejected (e.g., last admin)
      if (errorMessage.includes('last admin')) {
        setError('Cannot delete the last admin user.');
      } else {
        setError(`Failed to delete user: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = () => {
    setPage(0); // Reset to first page
    // No need to call fetchUsers explicitly as it's now in the useEffect dependencies
  };
  
  const handleClearSearch = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    setPage(0);
    // No need to call fetchUsers explicitly as it's now in the useEffect dependencies
  };  const handleRefresh = () => {
    // Force a refresh using our dedicated method
    console.log('Manual refresh triggered');
    forceRefresh();
  };
  
  // Force a refresh by resetting state and calling fetchUsers
  const forceRefresh = () => {
    setPage(0);
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
    
    // Add a small delay to ensure state is updated
    setTimeout(() => {
      fetchUsers();
    }, 300);
  };
  
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  
  // Method to check if the API is available and working
  const checkApiConnection = async () => {
    try {
      // Make a simple request to check if API is reachable
      await AdminService.getCurrentUser();
      return true;
    } catch (err) {
      console.error('API connection check failed:', err);
      return false;
    }
  };
  
  // Toggle debug mode
  const toggleDebugMode = () => {
    setDebugMode(prev => !prev);
  };
  
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>User Management</Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0); // Reset to first page when search changes
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      &times;
                    </IconButton>
                  </InputAdornment>
                )
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPage(0); // Reset to first page when filter changes
                }}
                label="Role"
              >
                <MenuItem value="">All Roles</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="auditor">Auditor</MenuItem>
                <MenuItem value="editor">Editor</MenuItem>
                <MenuItem value="viewer">Viewer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0); // Reset to first page when filter changes
                }}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleClearSearch}
            >
              Clear
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenUserDialog('add')}
            >
              Add User
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.roles.map(role => (
                          <Chip
                            key={role}
                            label={role}
                            size="small"
                            color={role === 'admin' ? 'primary' : 
                                  role === 'auditor' ? 'secondary' : 
                                  role === 'editor' ? 'info' : 'default'}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>                    <TableCell>
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenUserDialog('edit', user)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(user)}
                        disabled={user.username === 'admin'} // Prevent deleting the main admin
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Add/Edit User Dialog */}
      <Dialog
        open={openUserDialog}
        onClose={handleCloseUserDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {dialogMode === 'add' ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AddIcon color="primary" />
                <Typography variant="h6" component="span">
                  Create New User
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Add a new user account to the system
              </Typography>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="primary" />
                <Typography variant="h6" component="span">
                  Edit User Account
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Update user information and permissions
              </Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
            {/* Username Field */}
            <Grid item xs={12}>
              <TextField
                name="username"
                label="Username"
                value={userForm.username}
                onChange={handleUserFormChange}
                fullWidth
                required
                error={!!formErrors.username}
                helperText={
                  formErrors.username || 
                  (dialogMode === 'edit' ? 'Username cannot be changed' : 'Choose a unique username for login')
                }
                disabled={dialogMode === 'edit'}
                autoFocus={dialogMode === 'add'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color={formErrors.username ? 'error' : 'action'} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Email Field */}
            <Grid item xs={12}>
              <TextField
                name="email"
                label="Email Address"
                type="email"
                value={userForm.email}
                onChange={handleUserFormChange}
                fullWidth
                required
                error={!!formErrors.email}
                helperText={formErrors.email || 'Enter a valid email address'}
                placeholder="user@example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color={formErrors.email ? 'error' : 'action'} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Password Field */}
            <Grid item xs={12}>
              <TextField
                name="password"
                label={dialogMode === 'add' ? 'Password' : 'New Password'}
                type={showPassword ? 'text' : 'password'}
                value={userForm.password}
                onChange={handleUserFormChange}
                fullWidth
                required={dialogMode === 'add'}
                error={!!formErrors.password}
                helperText={
                  formErrors.password || 
                  (dialogMode === 'add' 
                    ? 'Minimum 8 characters required' 
                    : 'Leave blank to keep current password')
                }
                placeholder={dialogMode === 'add' ? '••••••••' : 'Enter new password (optional)'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color={formErrors.password ? 'error' : 'action'} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        size="small"
                        title={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            {/* Confirm Password Field */}
            <Grid item xs={12}>
              <TextField
                name="confirmPassword"
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={userForm.confirmPassword}
                onChange={handleUserFormChange}
                fullWidth
                required={dialogMode === 'add' || userForm.password.length > 0}
                error={!!formErrors.confirmPassword}
                helperText={formErrors.confirmPassword || 'Re-enter password to confirm'}
                placeholder="••••••••"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color={formErrors.confirmPassword ? 'error' : 'action'} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Roles Field */}
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!formErrors.roles}>
                <InputLabel>User Roles</InputLabel>
                <Select
                  name="roles"
                  multiple
                  value={userForm.roles}
                  onChange={handleUserFormChange}
                  label="User Roles"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={value.charAt(0).toUpperCase() + value.slice(1)} 
                          size="small"
                          color={
                            value === 'admin' ? 'primary' :
                            value === 'auditor' ? 'secondary' :
                            value === 'editor' ? 'info' : 'default'
                          }
                        />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="admin">
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">Admin</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Full system access and user management
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="auditor">
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">Auditor</Typography>
                      <Typography variant="caption" color="text.secondary">
                        View audit logs and system reports
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="editor">
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">Editor</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Create and modify content
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="viewer">
                    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">Viewer</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Read-only access to content
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
                {formErrors.roles ? (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.roles}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                    Select one or more roles for this user
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            {/* Account Status */}
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 1,
                bgcolor: 'background.paper'
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="isActive"
                      checked={userForm.isActive}
                      onChange={handleUserFormChange}
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Account Status: {userForm.isActive ? 'Active' : 'Inactive'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {userForm.isActive 
                          ? 'User can log in and access the system' 
                          : 'User account is disabled and cannot log in'}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button 
            onClick={handleCloseUserDialog}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveUser} 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={dialogMode === 'add' ? <AddIcon /> : <SaveIcon />}
          >
            {loading ? 'Saving...' : dialogMode === 'add' ? 'Create User' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the user "{userToDelete?.username}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
      
      {/* Debug button - only visible in development */}
      {process.env.NODE_ENV !== 'production' && (
        <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 2000 }}>
          <Button 
            variant="outlined" 
            color="info" 
            onClick={toggleDebugMode}
            sx={{ opacity: 0.8 }}
          >
            {debugMode ? 'Hide Debug' : 'Debug Mode'}
          </Button>
        </Box>
      )}
      
      {/* Debug panel */}
      <Dialog open={debugMode} onClose={toggleDebugMode} maxWidth="md" fullWidth>
        <DialogTitle>
          Debug Information
          <IconButton
            aria-label="close"
            onClick={toggleDebugMode}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            &times;
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>User State</Typography>
          <pre style={{ overflow: 'auto', maxHeight: 200, backgroundColor: '#f5f5f5', padding: 10 }}>
            {JSON.stringify(users, null, 2)}
          </pre>
          
          <Typography variant="h6" gutterBottom mt={2}>Filters</Typography>
          <pre style={{ overflow: 'auto', backgroundColor: '#f5f5f5', padding: 10 }}>
            {JSON.stringify({
              page,
              rowsPerPage,
              searchTerm,
              roleFilter,
              statusFilter
            }, null, 2)}
          </pre>
          
          <Typography variant="h6" gutterBottom mt={2}>Actions</Typography>
          <Button variant="contained" onClick={forceRefresh} sx={{ mr: 1 }}>
            Force Refresh
          </Button>
          <Button variant="outlined" onClick={() => fetchUsers()} sx={{ mr: 1 }}>
            Fetch Users
          </Button>
          <Button variant="outlined" color="error" onClick={() => setUsers([])}>
            Clear Users
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserManagement;